<script setup lang="ts">
import { computed, ref, type Ref } from "vue";
import { useTranslation } from "i18next-vue";
import type { MergedReportImage } from "@/types/report-images.types";
import { useDeleteDiagnosticImage } from "@/stores/queries";
import {
  useExcludeReportImage,
  useRestoreReportImage,
  useSortReportImageToSection,
  useCreateReportImage,
} from "@/stores/queries/report-images.query";
import DRBtnIcon from "@/components/UI/DRBtnIcon.vue";
import DRDropdownItem from "@/components/UI/DRDropdownItem.vue";
import MoveSectionModal from "./MoveSectionModal.vue";
import EditReportImageDialog from "./EditReportImageDialog.vue";

const props = withDefaults(
  defineProps<{
    image: MergedReportImage;
    diagnosticId: number;
    reportId: number;
    buttonSize?: number;
  }>(),
  {
    buttonSize: 28,
  },
);

const emit = defineEmits<{
  (e: "action-performed"): void;
}>();

const { t } = useTranslation();

const diagnosticIdRef = computed(() => props.diagnosticId) as Ref<number>;
const reportIdRef = computed(() => props.reportId) as Ref<number>;

// Mutations
const { mutate: deleteImage, isPending: isDeleting } =
  useDeleteDiagnosticImage(diagnosticIdRef);
const { mutateAsync: excludeImage, isPending: isExcluding } =
  useExcludeReportImage(reportIdRef);
const { mutateAsync: restoreImage, isPending: isRestoring } =
  useRestoreReportImage(reportIdRef);
const { mutateAsync: sortToSection, isPending: isSorting } =
  useSortReportImageToSection(reportIdRef);
const { mutateAsync: createReportImage, isPending: isCreating } =
  useCreateReportImage(reportIdRef);

// Edit dialog state
const showEditDialog = ref(false);

// Move dialog state
const showMoveDialog = ref(false);

// Helper to get or create reportImageId
const ensureReportImageId = async (): Promise<number | null> => {
  if (props.image.reportImageId) return props.image.reportImageId;
  if (props.image.isVirtual) {
    const newReportImage = await createReportImage({
      diagnosticImageId: props.image.diagnosticImageId,
      reportId: props.reportId,
    });
    return newReportImage.id;
  }
  return null;
};

const moveImageToSection = async (sectionId: number | null) => {
  const reportImageId = await ensureReportImageId();
  if (reportImageId) {
    await sortToSection({ imageId: reportImageId, sectionId });
    emit("action-performed");
  }
  showMoveDialog.value = false;
};

// Actions
const handleDelete = () => {
  // Delete the DiagnosticImage (will cascade to ReportImage)
  deleteImage(props.image.diagnosticImageId, {
    onSuccess: () => emit("action-performed"),
  });
};

const handleExclude = async () => {
  const reportImageId = await ensureReportImageId();
  if (reportImageId) {
    await excludeImage(reportImageId);
    emit("action-performed");
  }
};

const handleRestore = async () => {
  if (props.image.reportImageId) {
    await restoreImage(props.image.reportImageId);
    emit("action-performed");
  }
};
</script>

<template>
  <el-dropdown trigger="click">
    <DRBtnIcon
      :size="buttonSize"
      icon="bi-three-dots"
      variant="flat"
      @click.stop
    />
    <template #dropdown>
      <el-dropdown-menu>
        <DRDropdownItem
          icon="bi-pencil"
          :label="t('report:photos.cardActions.edit')"
          @select="showEditDialog = true"
        />
        <DRDropdownItem
          icon="bi-folder-symlink"
          :label="t('report:photos.cardActions.move')"
          :loading="isSorting || isCreating"
          @select="showMoveDialog = true"
        />
        <DRDropdownItem
          v-if="!image.excluded"
          icon="bi-eye-slash"
          :label="t('report:photos.cardActions.ignore')"
          :loading="isExcluding || isCreating"
          @select="handleExclude"
        />
        <DRDropdownItem
          v-else
          icon="bi-eye"
          :label="t('report:photos.action.restore')"
          :loading="isRestoring"
          @select="handleRestore"
        />
        <DRDropdownItem
          icon="bi-trash"
          :label="t('report:photos.cardActions.delete')"
          type="danger"
          :loading="isDeleting"
          @select="handleDelete"
        />
      </el-dropdown-menu>
    </template>
  </el-dropdown>

  <!-- Edit dialog -->
  <EditReportImageDialog
    v-model="showEditDialog"
    :image="image"
    :report-id="reportId"
  />

  <!-- Move to section dialog -->
  <MoveSectionModal
    v-model="showMoveDialog"
    :report-id="reportId"
    @select="moveImageToSection"
  />
</template>
